import ProductCard from "@/components/Ui/Pages/ProductCard/ProductCard";
import RangeSlider from "@/components/Ui/Tools/RangeSlider/RangeSlider";
import { useCount, useFilter } from "@/global-state/global.state";
import { getSubMenuItem, t, upperCaseFirstLetter, localePrefix } from "@/helper/helper";
import useProduct from "@/hooks/common/useProduct";
import { baseURL } from "@/services/api";
import {
  IBrandsGeneric,
  IPagination,
  IProduct,
  IProductDataGeneric,
} from "global-types/global.types";
import { GetServerSideProps } from "next";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useRouter } from "next/router";
import nookies from "nookies";
import { useMedia } from "react-use-media";
import { SITE_NAME, SITE_URL } from "@/helper/constant";
import { NextSeo } from "next-seo";
import axios from "axios";
import useBrandsFilter from "@/hooks/common/useBrandsFilter";
import useHeader from "@/hooks/common/useHeader";
import useBreadCrumbs from "@/hooks/tools/useBreadCrumb";
import Link from "next/link";
import { useBottomScrollListener } from "react-bottom-scroll-listener";
import Spinner from "@/components/Ui/Tools/Spinner/Spinner";
import Head from "next/head";

interface Props {
  locale: string;
  slug: string;
  productData: IProduct<IProductDataGeneric<IBrandsGeneric>[], IPagination>;
}

export default function ProductList({ locale, slug, productData }: Props) {
  const {
    brands,
    rate,
    price,
    is_discount,
    is_new,
    setFilter,
    setBrands,
    destroyFilter,
  } = useFilter();

  const [paginateData, setPaginateData] = useState<
    IProductDataGeneric<IBrandsGeneric>[]
  >(productData.data);

  const { count, setCount, destroyCount } = useCount();
  const router = useRouter();
  const { asPath } = router;

  // api
  const { data, isFetching } = useProduct({
    initialData: productData,
    setPaginationData: setPaginateData,
    params: {
      category: slug,
      brands,
      rate,
      price,
      is_discount,
      is_new,
      page: count,
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.currentTarget;

    if (name === "is_new" || name === "is_discount") {
      setFilter({
        [name]: checked ? "1" : null,
      });
    } else if (name === "is_discount") {
      setFilter({
        [name]: checked ? "1" : null,
        is_new: null,
      });
    } else {
      setFilter({
        [name]: value,
      });
    }

    destroyCount();
  };

  const handleChangeBrands = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.currentTarget;

    if (checked) {
      setBrands({
        brands: [value],
      });
    } else {
      const index = brands.indexOf(value);
      if (index > -1) {
        brands.splice(index, 1);

        setBrands({
          brands: brands,
          type: "delete",
        });
      }
    }

    destroyCount();
  };

  useEffect(() => {
    setPaginateData(productData.data);
    destroyFilter();
    destroyCount();
  }, [slug, productData.data, destroyFilter, destroyCount]);

  useEffect(() => {
    const handleRouteChangeComplete = () => {
      setPaginateData(productData.data);
      destroyFilter();
      destroyCount();
    };

    router.events.on("routeChangeComplete", handleRouteChangeComplete);

    return () => {
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
      destroyFilter();
      destroyCount();
    };
  }, [router.events, productData.data, destroyFilter, destroyCount]);

  const mainSectionRef = useRef<HTMLDivElement>(null);

  const calculateOffset = () => {
    if (mainSectionRef.current) {
      return mainSectionRef.current.offsetHeight;
    }
    return 100;
  };

  useBottomScrollListener(
    function () {
      if (data && data.data.length !== 0 && !isFetching) {
        if (count <= data?.pagination?.last_page) {
          setCount(1);
        }
      }
    },
    {
      offset: calculateOffset(),
      debounce: 1000,
      triggerOnNoScroll: true,
    },
  );

  const isMobile = useMedia({
    maxWidth: 767,
  });

  const [isShowFilter, setIsShowFilter] = useState<boolean>(false);

  // brands for filter
  const { brandsFilter, isLoadingBrandsFilter } = useBrandsFilter(slug);

  const { data: headerData } = useHeader();

  const { breadCrumbs } = useBreadCrumbs(slug);

  // Create breadcrumb schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: t("homePage", locale),
        item: `${SITE_URL}${localePrefix(locale)}`,
      },
      ...(breadCrumbs?.data.parent
        ? breadCrumbs.data.parent
            .slice()
            .reverse()
            .map((item, index) => ({
              "@type": "ListItem",
              position: index + 2,
              name: item.name,
              item: `${SITE_URL}${localePrefix(locale)}/categories/${item.slug}`,
            }))
        : []),
      {
        "@type": "ListItem",
        position: (breadCrumbs?.data.parent?.length || 0) + 2,
        name: headerData ? getSubMenuItem(headerData.data, slug).name : "",
        item: `${SITE_URL}${localePrefix(locale)}/product-list/${slug}`,
      },
    ],
  };

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
        />
      </Head>
      <NextSeo
        title={`${productData?.meta?.data?.meta?.title}`}
        description={productData?.meta?.data?.meta?.description || ""}
        canonical={SITE_URL + localePrefix(locale) + asPath}
        additionalMetaTags={[
          {
            name: "title",
            content: `${
              productData?.meta?.data?.meta?.title || upperCaseFirstLetter(slug)
            }`,
          },
        ]}
        openGraph={{
          url: SITE_URL + localePrefix(locale) + asPath,
          title: `${
            productData?.meta?.data?.meta?.title || upperCaseFirstLetter(slug)
          }`,
          description: productData?.meta?.data?.meta?.description || "",
          images: [
            {
              url: productData?.meta?.data?.og_img || "",
            },
          ],
          siteName: SITE_NAME,
        }}
      />
      <Wrapper>
        <div className="product-list">
          <div className="product-list-body">
            <div className="breadcrumb">
              <Link href={`${localePrefix(locale) || "/"}`}>{t("homePage", locale)}</Link>
              <span className="breadcrumb-seperator" />
              {breadCrumbs?.data.parent ? (
                breadCrumbs.data.parent
                  .slice()
                  .reverse()
                  .map((item, index) => (
                    <Link
                      href={`${localePrefix(locale)}/categories/${item.slug}`}
                      key={index.toString() + item.name}
                    >
                      {item.name}
                    </Link>
                  ))
              ) : (
                <></>
              )}
              <span className="breadcrumb-seperator" />
              <Link
                href={`${localePrefix(locale)}/product-list/${slug}`}
                className="capital"
              >
                {headerData ? getSubMenuItem(headerData.data, slug).name : ""}
              </Link>
            </div>
            <div
              className={`mobile-sidebar ${isShowFilter ? "active" : ""}`}
              onClick={() => {
                if (isMobile) {
                  setIsShowFilter((prev) => !prev);
                }
              }}
            >
              <div className="mobile-sidebar-header">
                <h5>
                  <svg
                    width={18}
                    height={18}
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17 1H1L7.4 9.40889V15.2222L10.6 17V9.40889L17 1Z"
                      stroke="#1B1C1F"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Filter
                </h5>
                <p>
                  {data?.data.length}
                  <svg
                    width={14}
                    height={9}
                    viewBox="0 0 14 9"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="filter-svg"
                  >
                    <path
                      d="M1 1.82422L7 7.81891L13 1.82422"
                      stroke="#1B1C1F"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </p>
              </div>
              <div className="side-section">
                <h2>Filter</h2>
                <div className="sidebar-category-card">
                  <label
                    htmlFor="is_discount"
                    className="custom-checkbox undefined"
                  >
                    <input
                      type="checkbox"
                      id="is_discount"
                      name="is_discount"
                      className="custom-checkbox__input"
                      defaultChecked={false}
                      onChange={handleChange}
                    />
                    {t("discounted-product", locale)}
                  </label>
                  <label htmlFor="is_new" className="custom-checkbox undefined">
                    <input
                      type="checkbox"
                      id="is_new"
                      name="is_new"
                      className="custom-checkbox__input"
                      defaultChecked={false}
                      onChange={handleChange}
                    />
                    {t("newProducts", locale)}
                  </label>
                </div>
                <article className="question acc-side">
                  <header>
                    <div className="header-wrapper">
                      <h3>{t("brands", locale)}</h3>
                    </div>
                    <button className="btn null">
                      <svg
                        width={14}
                        height={9}
                        viewBox="0 0 14 9"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1 1.82422L7 7.81891L13 1.82422"
                          stroke="#1B1C1F"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </header>

                  <div
                    className="acc-cont"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: isLoadingBrandsFilter
                        ? "center"
                        : "start",
                      alignItems: isLoadingBrandsFilter ? "center" : "start",
                    }}
                  >
                    {isLoadingBrandsFilter ? (
                      <Spinner width={30} height={30} />
                    ) : (
                      brandsFilter?.data.map((item, index) => (
                        <label
                          htmlFor={item.name}
                          className="custom-checkbox"
                          key={index.toString() + item.name}
                        >
                          <input
                            type="checkbox"
                            id={item.name}
                            name="brands"
                            className="custom-checkbox__input"
                            value={item.id}
                            onChange={handleChangeBrands}
                          />
                          {item.name}
                        </label>
                      ))
                    )}
                  </div>
                </article>
                <article className="question acc-side">
                  <header>
                    <div className="header-wrapper">
                      <h3>{t("rating", locale)}</h3>
                    </div>
                    <button className="btn null">
                      <svg
                        width={14}
                        height={9}
                        viewBox="0 0 14 9"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1 1.82422L7 7.81891L13 1.82422"
                          stroke="#1B1C1F"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </header>
                  <div className="acc-cont">
                    <label
                      htmlFor="5rate"
                      className="custom-checkbox undefined"
                    >
                      <input
                        type="radio"
                        id="5rate"
                        name="rate"
                        className="custom-checkbox__input"
                        value={5}
                        onChange={handleChange}
                      />
                      <span>
                        5{" "}
                        <svg
                          width={12}
                          height={12}
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M6 0L7.854 3.75205L12 4.35741L9 7.27634L9.708 11.4L6 9.45205L2.292 11.4L3 7.27634L0 4.35741L4.146 3.75205L6 0Z"
                            fill="#FED500"
                          />
                        </svg>
                        <svg
                          width={12}
                          height={12}
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M6 0L7.854 3.75205L12 4.35741L9 7.27634L9.708 11.4L6 9.45205L2.292 11.4L3 7.27634L0 4.35741L4.146 3.75205L6 0Z"
                            fill="#FED500"
                          />
                        </svg>
                        <svg
                          width={12}
                          height={12}
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M6 0L7.854 3.75205L12 4.35741L9 7.27634L9.708 11.4L6 9.45205L2.292 11.4L3 7.27634L0 4.35741L4.146 3.75205L6 0Z"
                            fill="#FED500"
                          />
                        </svg>
                        <svg
                          width={12}
                          height={12}
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M6 0L7.854 3.75205L12 4.35741L9 7.27634L9.708 11.4L6 9.45205L2.292 11.4L3 7.27634L0 4.35741L4.146 3.75205L6 0Z"
                            fill="#FED500"
                          />
                        </svg>
                        <svg
                          width={12}
                          height={12}
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M6 0L7.854 3.75205L12 4.35741L9 7.27634L9.708 11.4L6 9.45205L2.292 11.4L3 7.27634L0 4.35741L4.146 3.75205L6 0Z"
                            fill="#FED500"
                          />
                        </svg>
                      </span>
                    </label>
                    <label
                      htmlFor="4rate"
                      className="custom-checkbox undefined"
                    >
                      <input
                        type="radio"
                        id="4rate"
                        name="rate"
                        className="custom-checkbox__input"
                        value={4}
                        onChange={handleChange}
                      />
                      <span>
                        4{" "}
                        <svg
                          width={12}
                          height={12}
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M6 0L7.854 3.75205L12 4.35741L9 7.27634L9.708 11.4L6 9.45205L2.292 11.4L3 7.27634L0 4.35741L4.146 3.75205L6 0Z"
                            fill="#FED500"
                          />
                        </svg>
                        <svg
                          width={12}
                          height={12}
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M6 0L7.854 3.75205L12 4.35741L9 7.27634L9.708 11.4L6 9.45205L2.292 11.4L3 7.27634L0 4.35741L4.146 3.75205L6 0Z"
                            fill="#FED500"
                          />
                        </svg>
                        <svg
                          width={12}
                          height={12}
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M6 0L7.854 3.75205L12 4.35741L9 7.27634L9.708 11.4L6 9.45205L2.292 11.4L3 7.27634L0 4.35741L4.146 3.75205L6 0Z"
                            fill="#FED500"
                          />
                        </svg>
                        <svg
                          width={12}
                          height={12}
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M6 0L7.854 3.75205L12 4.35741L9 7.27634L9.708 11.4L6 9.45205L2.292 11.4L3 7.27634L0 4.35741L4.146 3.75205L6 0Z"
                            fill="#FED500"
                          />
                        </svg>
                      </span>
                    </label>
                    <label
                      htmlFor="3rate"
                      className="custom-checkbox undefined"
                    >
                      <input
                        type="radio"
                        id="3rate"
                        name="rate"
                        className="custom-checkbox__input"
                        value={3}
                        onChange={handleChange}
                      />
                      <span>
                        3{" "}
                        <svg
                          width={12}
                          height={12}
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M6 0L7.854 3.75205L12 4.35741L9 7.27634L9.708 11.4L6 9.45205L2.292 11.4L3 7.27634L0 4.35741L4.146 3.75205L6 0Z"
                            fill="#FED500"
                          />
                        </svg>{" "}
                        <svg
                          width={12}
                          height={12}
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M6 0L7.854 3.75205L12 4.35741L9 7.27634L9.708 11.4L6 9.45205L2.292 11.4L3 7.27634L0 4.35741L4.146 3.75205L6 0Z"
                            fill="#FED500"
                          />
                        </svg>
                        <svg
                          width={12}
                          height={12}
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M6 0L7.854 3.75205L12 4.35741L9 7.27634L9.708 11.4L6 9.45205L2.292 11.4L3 7.27634L0 4.35741L4.146 3.75205L6 0Z"
                            fill="#FED500"
                          />
                        </svg>
                      </span>
                    </label>
                    <label
                      htmlFor="2rate"
                      className="custom-checkbox undefined"
                    >
                      <input
                        type="radio"
                        id="2rate"
                        name="rate"
                        className="custom-checkbox__input"
                        value={2}
                        onChange={handleChange}
                      />
                      <span>
                        2{" "}
                        <svg
                          width={12}
                          height={12}
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M6 0L7.854 3.75205L12 4.35741L9 7.27634L9.708 11.4L6 9.45205L2.292 11.4L3 7.27634L0 4.35741L4.146 3.75205L6 0Z"
                            fill="#FED500"
                          />
                        </svg>
                        <svg
                          width={12}
                          height={12}
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M6 0L7.854 3.75205L12 4.35741L9 7.27634L9.708 11.4L6 9.45205L2.292 11.4L3 7.27634L0 4.35741L4.146 3.75205L6 0Z"
                            fill="#FED500"
                          />
                        </svg>
                      </span>
                    </label>
                    <label
                      htmlFor="1rate"
                      className="custom-checkbox undefined"
                    >
                      <input
                        type="radio"
                        id="1rate"
                        name="rate"
                        className="custom-checkbox__input"
                        value={1}
                        onChange={handleChange}
                      />
                      <span>
                        1{" "}
                        <svg
                          width={12}
                          height={12}
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M6 0L7.854 3.75205L12 4.35741L9 7.27634L9.708 11.4L6 9.45205L2.292 11.4L3 7.27634L0 4.35741L4.146 3.75205L6 0Z"
                            fill="#FED500"
                          />
                        </svg>
                      </span>
                    </label>
                  </div>
                </article>
                <article className="question acc-side">
                  <header>
                    <div className="header-wrapper">
                      <h3>{t("priceRange", locale)}</h3>
                    </div>
                    <button className="btn null">
                      <svg
                        width={14}
                        height={9}
                        viewBox="0 0 14 9"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1 1.82422L7 7.81891L13 1.82422"
                          stroke="#1B1C1F"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </header>
                  <div className="acc-cont">
                    <RangeSlider locale={locale} value={data?.filter?.range} />
                  </div>
                </article>
              </div>
            </div>

            <div className="category-main-section-div" ref={mainSectionRef}>
              <div className="category-class-name">
                <h1 className="category-page-title">
                  {headerData ? (
                    getSubMenuItem(headerData?.data, slug)?.menu_name
                  ) : (
                    <></>
                  )}
                </h1>
              </div>
              <div className="main-section" id="mainSection">
                {isFetching
                  ? Array.from({ length: 16 }).map((_, i) => (
                      <SkeletonCard key={i}>
                        <div className="skeleton-img" />
                        <div className="skeleton-title" />
                        <div className="skeleton-price" />
                      </SkeletonCard>
                    ))
                  : paginateData?.map((item, index) => (
                      <ProductCard
                        slug={asPath?.replaceAll("/product-list", "")}
                        locale={locale}
                        data={item}
                        md=""
                        key={index.toString() + item.title}
                      />
                    ))}
              </div>
            </div>
          </div>
          <Flex>{isFetching && <Spinner width={50} height={50} />}</Flex>
          <div
            className="flex site-header"
            style={{
              height: "auto",
            }}
          >
            <p
              className="category-page-desc"
              style={{
                fontSize: "14px",
              }}
              dangerouslySetInnerHTML={{
                __html: breadCrumbs?.data.desc || "",
              }}
            />
          </div>
        </div>
      </Wrapper>
    </>
  );
}

const SkeletonCard = styled.div`
  @keyframes shimmer {
    0% {
      background-position: -400px 0;
    }
    100% {
      background-position: 400px 0;
    }
  }

  .skeleton-img,
  .skeleton-title,
  .skeleton-price {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 800px 100%;
    animation: shimmer 1.5s infinite ease-in-out;
    border-radius: 6px;
  }

  .skeleton-img {
    width: 100%;
    aspect-ratio: 1;
    border-radius: 8px;
  }

  .skeleton-title {
    height: 16px;
    width: 75%;
    margin-top: 12px;
  }

  .skeleton-price {
    height: 20px;
    width: 40%;
    margin-top: 8px;
  }
`;

const Flex = styled.div`
  display: flex;
  justify-content: center;
`;

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { locale } = ctx;
  const { slug } = ctx.query;
  const _slug = slug as string[];
  const { auth } = nookies.get(ctx);

  try {
    // products
    const authHeaders = auth
      ? {
          authorization: "Bearer " + auth,
        }
      : null;

    const productData = await axios.get(
      baseURL + `/product${auth ? "/user" : ""}?category=${_slug}`,
      {
        headers: {
          "x-localization": locale ? locale : "az",
          ...authHeaders,
        },
      },
    );

    return {
      props: {
        locale,
        slug,
        productData: productData.data,
      },
    };
  } catch (error: any) {
    return {
      notFound: true,
    };
  }
};

const Wrapper = styled.div``;
